/**
 * React Query hooks for entity management
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchEntities,
  fetchEntity,
  fetchEntitySummary,
  createEntity,
  updateEntity,
  deleteEntity,
} from '@/api/entities';
import type { Entity } from '@/types/entities';

/**
 * Fetch all entities
 */
export function useEntities() {
  return useQuery({
    queryKey: ['entities'],
    queryFn: fetchEntities,
  });
}

/**
 * Fetch a single entity by ID
 */
export function useEntity(id: string) {
  return useQuery({
    queryKey: ['entities', id],
    queryFn: () => fetchEntity(id),
    enabled: !!id, // Only run if id is provided
  });
}

/**
 * Fetch entity summary (with balance and transactions)
 */
export function useEntitySummary(id: string) {
  return useQuery({
    queryKey: ['entities', id, 'summary'],
    queryFn: () => fetchEntitySummary(id),
    enabled: !!id,
  });
}

/**
 * Create a new entity
 */
export function useCreateEntity() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createEntity,
    onSuccess: () => {
      // Invalidate entities list to refetch
      queryClient.invalidateQueries({ queryKey: ['entities'] });
    },
  });
}

/**
 * Update an entity
 */
export function useUpdateEntity() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, entity }: { id: string; entity: Partial<Entity> }) =>
      updateEntity(id, entity),
    onSuccess: (data) => {
      // Invalidate specific entity and entities list
      queryClient.invalidateQueries({ queryKey: ['entities'] });
      queryClient.invalidateQueries({ queryKey: ['entities', data.id] });
    },
  });
}

/**
 * Delete an entity
 */
export function useDeleteEntity() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteEntity,
    onSuccess: () => {
      // Invalidate entities list to refetch
      queryClient.invalidateQueries({ queryKey: ['entities'] });
    },
  });
}
